import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Paper, Typography, TextField, Button, Box, Alert,
  Tabs, Tab, InputAdornment, IconButton, Stepper, Step, StepLabel,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Security, School } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

function TabPanel({ children, value, index }) {
  return value === index && <Box sx={{ pt: 3 }}>{children}</Box>;
}

const AdminLogin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Login state
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState(0); // 0=credentials, 1=OTP
  const [loginEmail, setLoginEmail] = useState('');
  const [loginOTP, setLoginOTP] = useState('');
  const [loginOTPError, setLoginOTPError] = useState('');

  // Registration state
  const [regStep, setRegStep] = useState(0);
  const [regForm, setRegForm] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [regErrors, setRegErrors] = useState({});
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const regSteps = ['Account Details', 'Verify Email'];

  // ---------- Login handlers ----------
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/login`, {
        username: loginForm.username,
        password: loginForm.password
      });
      setLoginEmail(res.data.email);
      toast.success('Verification code sent to your email!');
      setLoginStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid username or password');
    } finally { setIsLoading(false); }
  };

  const handleVerifyLoginOTP = async () => {
    if (!loginOTP.trim()) { setLoginOTPError('Enter the code'); return; }
    setIsLoading(true);
    setLoginOTPError('');
    try {
      const res = await axios.post(`${API_URL}/auth/login/verify`, {
        email: loginEmail,
        otpCode: loginOTP.trim()
      });
      const { token, user } = res.data;
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
      toast.success(`Welcome back, ${user.username || user.email}!`);
      if (user.role === 'super_admin' || user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/trainer');
      }
    } catch (err) {
      setLoginOTPError(err.response?.data?.error || 'Verification failed');
    } finally { setIsLoading(false); }
  };

  const handleBackToLogin = () => {
    setLoginStep(0);
    setLoginOTP('');
    setLoginOTPError('');
    setError('');
  };

  // ---------- Registration handlers ----------
  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegForm(prev => ({ ...prev, [name]: value }));
    if (regErrors[name]) setRegErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!regForm.fullName.trim()) e.fullName = 'Full name is required';
    if (!regForm.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(regForm.email)) e.email = 'Invalid email format';
    if (!regForm.password) e.password = 'Password is required';
    else if (regForm.password.length < 6) e.password = 'At least 6 characters';
    if (regForm.password !== regForm.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setRegErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSendOTP = async () => {
    setRegLoading(true);
    try {
      await axios.post(`${API_URL}/auth/send-otp`, { email: regForm.email });
      toast.success('Verification code sent to your email!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send code');
    } finally { setRegLoading(false); }
  };

  const handleVerifyAndRegister = async () => {
    if (!otpCode.trim()) { setOtpError('Enter the code'); return; }
    setRegLoading(true);
    setOtpError('');
    try {
      const res = await axios.post(`${API_URL}/auth/register`, {
        fullName: regForm.fullName,
        email: regForm.email,
        password: regForm.password,
        otpCode: otpCode.trim()
      });
      localStorage.setItem('adminToken', res.data.token);
      localStorage.setItem('adminUser', JSON.stringify(res.data.user));
      toast.success('Account created! Welcome.');
      navigate('/trainer');
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Registration failed');
    } finally { setRegLoading(false); }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Security sx={{ fontSize: 50, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4">Automated Evaluation System</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your account or create a new one
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); setOtpError(''); setLoginStep(0); setLoginOTP(''); setLoginOTPError(''); }}
          variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Sign In" />
          <Tab label="Create Account" />
        </Tabs>

        {/* ====== SIGN IN ====== */}
        <TabPanel value={tab} index={0}>
          {loginStep === 0 ? (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Box component="form" onSubmit={handleLogin} noValidate>
                <TextField fullWidth label="Username or Email" name="username" value={loginForm.username}
                  onChange={handleLoginChange} margin="normal" required disabled={isLoading} autoFocus />
                <TextField fullWidth label="Password" name="password" type={showPassword ? 'text' : 'password'}
                  value={loginForm.password} onChange={handleLoginChange} margin="normal" required disabled={isLoading}
                  InputProps={{ endAdornment: <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment> }} />
                <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 3, py: 1.5 }} disabled={isLoading}>
                  {isLoading ? 'Sending code...' : 'Sign In'}
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                A verification code was sent to <strong>{loginEmail}</strong>
              </Alert>
              {loginOTPError && <Alert severity="error" sx={{ mb: 2 }}>{loginOTPError}</Alert>}
              <TextField sx={{ maxWidth: 280, mx: 'auto', mb: 2 }}
                label="Verification Code" value={loginOTP}
                onChange={e => { setLoginOTP(e.target.value); setLoginOTPError(''); }}
                error={!!loginOTPError} helperText={loginOTPError}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px' } }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                <Button variant="outlined" onClick={handleBackToLogin}>Back</Button>
                <Button variant="contained" onClick={handleVerifyLoginOTP} disabled={isLoading || loginOTP.length < 6}>
                  {isLoading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
                </Button>
              </Box>
              <Button size="small" onClick={handleLogin} disabled={isLoading}>Resend Code</Button>
            </Box>
          )}
        </TabPanel>

        {/* ====== REGISTER ====== */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <School sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Trainer Registration</Typography>
            <Typography variant="body2" color="text.secondary">
              Create an account to start creating exams
            </Typography>
          </Box>

          <Stepper activeStep={regStep} sx={{ mb: 3 }}>
            {regSteps.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
          </Stepper>

          {regStep === 0 && (
            <Box>
              <TextField fullWidth label="Full Name" name="fullName" value={regForm.fullName}
                onChange={handleRegChange} error={!!regErrors.fullName}
                helperText={regErrors.fullName || `${regForm.fullName.length}/100`}
                margin="normal" required inputProps={{ maxLength: 100 }} placeholder="e.g., Dr. Jane Doe" />
              <TextField fullWidth label="Email Address" name="email" type="email" value={regForm.email}
                onChange={handleRegChange} error={!!regErrors.email}
                helperText={regErrors.email || 'Used for login and OTP'}
                margin="normal" required placeholder="you@institution.edu" />
              <TextField fullWidth label="Password" name="password" type="password" value={regForm.password}
                onChange={handleRegChange} error={!!regErrors.password}
                helperText={regErrors.password || 'Min 6 characters'}
                margin="normal" required inputProps={{ minLength: 6 }} />
              <TextField fullWidth label="Confirm Password" name="confirmPassword" type="password"
                value={regForm.confirmPassword} onChange={handleRegChange} error={!!regErrors.confirmPassword}
                helperText={regErrors.confirmPassword} margin="normal" required />
              <Button fullWidth variant="contained" size="large" sx={{ mt: 3, py: 1.5 }}
                onClick={() => { if (validateStep1()) { handleSendOTP(); setRegStep(1); } }}
                disabled={regLoading}>
                {regLoading ? <CircularProgress size={24} /> : 'Next — Verify Email'}
              </Button>
            </Box>
          )}

          {regStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                A 6-digit code was sent to <strong>{regForm.email}</strong>
              </Alert>
              <TextField sx={{ maxWidth: 280, mx: 'auto', mb: 2 }}
                label="Verification Code" value={otpCode}
                onChange={e => { setOtpCode(e.target.value); setOtpError(''); }}
                error={!!otpError} helperText={otpError}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px' } }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                <Button variant="contained" onClick={handleVerifyAndRegister} disabled={regLoading || otpCode.length < 6}>
                  {regLoading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
              </Box>
              <Button size="small" onClick={handleSendOTP} disabled={regLoading}>Resend Code</Button>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminLogin;